export interface CompressedImage {
  blob: Blob;
  dataUrl: string;
  width: number;
  height: number;
}

/**
 * Compress and downsize an image to a max width via canvas + JPEG re-encoding.
 * Default produces ~150-400 KB JPEGs from typical 2-4 MB phone photos.
 */
export async function compressImage(
  source: File | Blob,
  maxWidth = 1500,
  quality = 0.85,
): Promise<CompressedImage> {
  const url = URL.createObjectURL(source);
  try {
    const img = await loadImage(url);
    let { width, height } = img;
    if (width > maxWidth) {
      height = Math.round(height * (maxWidth / width));
      width = maxWidth;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('Canvas toBlob failed'))),
        'image/jpeg',
        quality,
      );
    });
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    return { blob, dataUrl, width, height };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}
